import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseLineFeedback } from '@/app/components/icons/BaseLineFeedback';
import { CollectBtn } from '@/app/components/shared/Community';
import { EllipsisDropdownMenu } from '@/app/components/shared/EllipsisDropdownMenu';
import LikeButton from '@/app/components/shared/LikeButton';
import { toast } from '@/app/components/ui/use-toast';
import { SITE, TRACE_EVENT_NAME } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { type ActionDialogType, CommunityEnum, EllipsisActionTypeEnum } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { getVideoMetadata } from '@/app/utils/client';
import type { ShortFilmInterface } from '../types';
import { getDisplayIpLocation } from '../utils';
import { ActivityInfo } from './ActivityInfo';
import { CommunityShareBtn } from './CommunityShareBtn';
import { FeedDetailMediaFrame } from './FeedDetailMediaFrame';
import { UserInfo } from './UserInfo';

export const ShortFilm = ({
  shortFilm,
  onVideoPlayEnd,
  handleCollect,
  handleUnCollect,
  onChangeEpisode,
  handleVote,
  handleUnVote,
  mediaAreaRef,
}: ShortFilmInterface) => {
  const t = useTranslations();
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const [currentShortFilmId, setCurrentShortFilmId] = useState<string>();
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const isActivity = shortFilm.isActivity;

  useEffect(() => {
    if (shortFilm.info?.id) {
      setCurrentShortFilmId(shortFilm.info?.id);
    }
  }, [shortFilm.info?.id]);

  const currentShortFilm = useMemo(() => {
    if (shortFilm.unit_type === 'single') {
      return {
        type: shortFilm.unit_type,
        ...shortFilm.info,
      };
    }
    if (currentShortFilmId && shortFilm.series?.length) {
      const targetShortFilm = shortFilm.series?.find((it) => it.id === currentShortFilmId);
      // 当前短片已下线
      if (!targetShortFilm) {
        // 播放第一个可用短片
        const id = shortFilm.series[0]?.id;
        if (id) {
          setCurrentShortFilmId(id);
          return undefined;
        }
        toast({
          message: t('shortFilm.disabled'),
          type: 'error',
        });
        return undefined;
      }
      return {
        type: shortFilm.unit_type,
        ...targetShortFilm,
      };
    }
    return undefined;
  }, [currentShortFilmId, shortFilm]);

  useEffect(() => {
    const fetchVideoMetadata = async () => {
      const metadata = await getVideoMetadata(currentShortFilm?.creation?.uri || '', {
        timeout: 15000,
        includeDuration: false,
      });
      setAspectRatio(metadata.aspectRatio);
    };
    fetchVideoMetadata();
  }, [currentShortFilm?.creation?.uri]);

  const handleActionDialog = (action: EllipsisActionTypeEnum) => {
    openActionDialog({
      action: action as ActionDialogType,
      id: currentShortFilm?.id || '',
      // TODO: 后续需要和后端一起优化，现在 type 全是 creation
      type: 'creation',
    });
  };

  /**
   * 媒体区域
   * @returns
   */
  const renderMediaArea = useCallback(() => {
    return (
      <FeedDetailMediaFrame
        src={currentShortFilm?.creation?.uri || ''}
        poster={currentShortFilm?.creation?.cover_uri}
        aspectRatio={aspectRatio}
        exploreTab={CommunityEnum.ShortFilm}
        hasAudio={true}
        defaultVolume={1}
        creationId={currentShortFilm?.id || ''}
        autoPlay={true}
        mediaAreaRef={mediaAreaRef as React.RefObject<HTMLDivElement | null>}
        onPlayEnd={onVideoPlayEnd}
      />
    );
  }, [
    aspectRatio,
    currentShortFilm?.creation?.cover_uri,
    currentShortFilm?.creation?.uri,
    currentShortFilm?.id,
    mediaAreaRef,
    onVideoPlayEnd,
  ]);

  /**
   * 短片信息栏
   * @returns
   */
  const renderInfo = () => {
    if (!currentShortFilm) return null;

    return (
      <>
        <div className="flex h-full w-[304px] flex-1 flex-col overflow-y-auto md:w-full">
          {shortFilm.info?.creator && (
            <UserInfo
              user={shortFilm.info.creator}
              ipLocation={getDisplayIpLocation(shortFilm.info.creator.id, shortFilm.ip_location)}
            />
          )}

          <div className="flex flex-1 flex-col pt-3">
            <div className="flex-shrink-0 font-medium text-sm text-system-white">{shortFilm.info?.title}</div>

            {shortFilm.info?.detail && (
              <p className="flex-shrink-0 pt-2 text-sm text-system-white64">{shortFilm.info?.detail}</p>
            )}

            {shortFilm.unit_type === 'series' && (
              <div className="flex flex-1 flex-col overflow-hidden">
                <div className="mt-3 flex-shrink-0 pb-3 font-medium text-base">{t('shortFilm.selections')}</div>
                <div className="flex-1 overflow-hidden pb-3">
                  <div className="hide-scrollbar grid max-h-full grid-cols-5 gap-1.5 overflow-y-scroll">
                    {shortFilm.series?.map((it) => (
                      <div
                        className={cn(
                          'flex aspect-square cursor-pointer items-center justify-center rounded-4 bg-white/10 hover:bg-system-white/20',
                          it.id === currentShortFilmId && 'bg-white/5',
                        )}
                        key={it.id}
                        onClick={() => {
                          if (it.id !== currentShortFilmId) {
                            setCurrentShortFilmId(it.id);
                            onChangeEpisode?.(it.id);
                            reportEvent(TRACE_EVENT_NAME[CommunityEnum.ShortFilm], {
                              episode_detail: it.episode_num || 1,
                              film_id: it.id,
                            });
                          }
                        }}
                      >
                        <div
                          className={cn(
                            it.id === currentShortFilmId &&
                              'bg-[linear-gradient(180deg,#5AE0FF_0%,#009BFF_100%)] bg-clip-text font-medium text-base text-transparent',
                          )}
                        >
                          {it.episode?.name ?? it.episode_num}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <span className="mt-3 text-system-white/50 text-xs">{SITE === 'china' && '内容由 AI 生成'}</span>

            {renderActivityInfo()}
          </div>
        </div>
        {currentShortFilm?.react_info && currentShortFilm.id && (
          <div className="mt-4 w-full">{renderCommonOperations()}</div>
        )}
      </>
    );
  };

  /**
   * 活动投稿的信息
   * 包括：活动名称、活动状态、奖项
   */
  const renderActivityInfo = () => {
    if (!shortFilm.activity) return null;
    return <ActivityInfo activity={shortFilm.activity} award={currentShortFilm?.award} className="mt-3 md:mt-2" />;
  };

  /**
   * 在信息栏最底部展示的通用操作按钮
   * 包括：收藏、分享、更多、点赞
   * @returns
   */
  const renderCommonOperations = () => {
    if (!currentShortFilm) return null;
    const { react_info } = currentShortFilm;

    return (
      <div className="flex h-10 items-center gap-2 md:h-12 md:gap-3">
        <CollectBtn
          key={currentShortFilm.id}
          id={currentShortFilm.id || ''}
          tabType={CommunityEnum.ShortFilm}
          itemCollect={react_info?.collected ?? false}
          itemCollectCount={react_info?.collect_count ?? 0}
          hideEmptyCollectCount={false}
          hideCount={true}
          handleCollect={handleCollect}
          handleUnCollect={handleUnCollect}
          className={cn(
            'flex aspect-square size-10 h-full cursor-pointer items-center justify-center rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12',
            isActivity && 'hidden',
          )}
          iconClassName="size-5 md:size-6"
        />
        <CommunityShareBtn id={currentShortFilm.id || ''} type={CommunityEnum.ShortFilm} isActivity={isActivity} />
        {SITE === 'china' && (
          <EllipsisDropdownMenu
            className="aspect-square size-10 h-full rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12 md:text-2xl"
            actions={[{ action: EllipsisActionTypeEnum.InspirationReport, icon: <BaseLineFeedback /> }]}
            onSelectedAction={handleActionDialog}
          />
        )}
        {react_info && (
          <LikeButton
            className="h-full flex-1 rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
            id={currentShortFilm.id || ''}
            itemVote={react_info?.voted || false}
            tabType={CommunityEnum.ShortFilm}
            itemVoteCount={react_info?.vote_count || 0}
            traceId={shortFilm.traceId || ''}
            handleVote={handleVote}
            handleUnVote={handleUnVote}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className="absolute inset-0 z-[-1] bg-black/60 bg-center bg-cover bg-no-repeat blur-[150px] md:hidden"
        style={{
          backgroundImage: `url('${currentShortFilm?.creation?.cover_uri}')`,
        }}
      >
        <div className="absolute inset-0 z-[-1] size-full bg-black/60" />
      </div>

      <div className="relative flex size-full flex-1 md:flex-col md:overflow-y-auto md:overflow-x-hidden">
        {renderMediaArea()}
        {/* 右侧信息栏 */}
        <div className="relative my-8 flex flex-col border-system-white10 border-l pr-[30px] pl-5 md:my-0 md:flex-1 md:border-0 md:px-4 md:pb-6">
          {renderInfo()}
        </div>
      </div>
    </>
  );
};
